import i18n from 'i18next';
import it from './locales/it.json';
import en from './locales/en.json';
import {
  resolveInitialLocale,
  setStoredLocale,
  type AppLocale,
} from './detect';

export type { AppLocale };

let ready = false;

export async function initI18n(): Promise<void> {
  if (ready) return;

  const lng = resolveInitialLocale();

  await i18n.init({
    lng,
    fallbackLng: 'it',
    supportedLngs: ['it', 'en'],
    resources: {
      it: { translation: it },
      en: { translation: en },
    },
    interpolation: { escapeValue: false },
  });

  document.documentElement.lang = lng;
  ready = true;
}

export function t(
  key: string,
  options?: Record<string, unknown>,
): string {
  return i18n.t(key, options);
}

export function getLocale(): AppLocale {
  const lng = i18n.language?.split('-')[0];
  return lng === 'en' ? 'en' : 'it';
}

export async function changeLanguage(locale: AppLocale): Promise<void> {
  if (getLocale() === locale) return;
  setStoredLocale(locale);
  await i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
  const { syncUserLocale } = await import('../auth/session');
  await syncUserLocale(locale);
}

export function onLanguageChanged(
  handler: (locale: AppLocale) => void,
): () => void {
  const cb = (lng: string) => {
    const locale: AppLocale = lng.startsWith('en') ? 'en' : 'it';
    handler(locale);
  };
  i18n.on('languageChanged', cb);
  return () => i18n.off('languageChanged', cb);
}

export { i18n };
